import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Result, Spin } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { useClient } from '../ClientProvider';
import { useClientConfigService } from '../../services/clients/ClientConfigServiceProvider';
import { ClientConfig, ClientSettings } from '../../model/client/ClientSettings';
import { useIsFortiusAdmin } from '../../hooks/useIsFortiusAdmin';
import { useHasPrivilegedRole } from '../../hooks/usePrivilegedRole';
import { useMemberOptional } from '../member/MemberContext';
import { forceTokenRefresh } from '../auth/tokenRefreshCoordinator';

interface ClientConfigProviderProps {
    children: React.ReactNode;
}

// The per-tenant `clients` claim stamped onto the token by the PostLogin action.
// Keep in sync with ClientProvider / ClientContextMiddleware.
const CLIENTS_CLAIM = 'fortius-app:clients';

// Upper bound on how long we keep the splash up waiting for MemberContext's
// post-login sync (client bootstrap + forced token refresh) to settle before we
// let the "not registered" error screen show. The happy-path sync is ~2-3s; this
// is a safety net so a genuinely hung/failed sync still surfaces the actionable
// Register/Retry/Log out screen instead of spinning forever.
const SYNC_SPLASH_MAX_MS = 15_000;

const ClientConfigContext = React.createContext<ClientConfig | undefined>(undefined);

export const ClientConfigProvider: React.FC<ClientConfigProviderProps> = ({ children }) => {
    const { isAuthenticated, user, getAccessTokenSilently, logout } = useAuth0();
    const { currentClientId } = useClient();
    const service = useClientConfigService();
    const isFortiusAdmin = useIsFortiusAdmin();
    // Staff/admins are refused self-registration server-side (their global roles
    // would otherwise apply to the new club), so don't even offer them the button.
    const canSelfRegister = !useHasPrivilegedRole();

    // Post-login provisioning is owned by MemberContext.syncAuth: it calls
    // `/auth/sync` (which bootstraps a brand-new signup's client into Auth0
    // app_metadata) and then forces the token refresh that lands the `clients`
    // claim. That flow races this gate's own config fetch — if we decide "not
    // registered" before the sync settles, the user gets a transient error
    // screen mid-signup. Read the sync's loading state (optional: undefined when
    // rendered without a MemberProvider, e.g. in unit tests) and hold the splash
    // until it settles rather than racing it. See the render guard below.
    const memberSyncing = useMemberOptional()?.isLoading ?? false;

    const [config, setConfig] = useState<ClientConfig | null>(null);
    const [hasError, setHasError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [attempt, setAttempt] = useState(0);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [registering, setRegistering] = useState(false);
    // Safety net: flips true once the member sync has been in flight longer than
    // SYNC_SPLASH_MAX_MS, so a hung sync can't trap the user on the splash. Reset
    // whenever the sync is not in flight.
    const [syncWaitExpired, setSyncWaitExpired] = useState(false);

    // FORT-219: on a brand-new first login the access token is minted with
    // `clients: []` — the tenant is provisioned moments later (PostLogin /
    // backend `/auth/sync` bootstrap) and a forced token refresh lands the
    // populated claim. `/clients/current/config` is tenancy-gated, so a fetch
    // with the pre-provision token 403s (getCurrentConfig resolves `undefined`).
    // We key the effect on this claim so the load re-runs automatically once the
    // tenant appears, instead of stranding the user on the error screen until a
    // manual reload.
    const assignedClients = Array.isArray((user as Record<string, unknown> | undefined)?.[CLIENTS_CLAIM])
        ? ((user as Record<string, string[]>)[CLIENTS_CLAIM])
        : [];
    const clientClaimKey = assignedClients.join('|');

    // Bound the stale-token recovery to a single forced refresh per attempt so a
    // genuinely unloadable config can't spin in a refresh loop.
    const triedForcedRefresh = useRef(false);

    useEffect(() => {
        // No fetch before authenticated or before a client is selected. Keep
        // the splash up rather than falling through to the error screen — these
        // states aren't a load failure, they're "not ready yet".
        if (!isAuthenticated || !currentClientId) {
            setLoading(true);
            setHasError(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setHasError(false);

        const load = async () => {
            try {
                let result = await service.getCurrentConfig();

                // Stale-token recovery: a token whose `clients` claim doesn't yet
                // include the current tenant 403s the tenancy-gated config
                // endpoint, which getCurrentConfig surfaces as `undefined`. This
                // is the normal first-login / just-reloaded window. Force one
                // token refresh and retry before showing the error screen — this
                // also covers a reload where nothing else triggers a refresh.
                if (result === undefined && !triedForcedRefresh.current) {
                    triedForcedRefresh.current = true;
                    try {
                        await forceTokenRefresh(getAccessTokenSilently);
                    } catch {
                        // No refresh token / refresh failed — fall through and let
                        // the retry decide whether to surface the error.
                    }
                    if (cancelled) return;
                    result = await service.getCurrentConfig();
                }

                if (cancelled) return;
                if (result === undefined) {
                    setHasError(true);
                    setConfig(null);
                } else {
                    setConfig(result);
                }
            } catch {
                if (cancelled) return;
                setHasError(true);
                setConfig(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        void load();

        return () => {
            cancelled = true;
        };
        // `memberSyncing` is a dependency so the config load re-runs once the
        // post-login sync settles — by then the refreshed token carries the
        // `clients` claim and the fetch succeeds without falling to the error
        // screen.
    }, [isAuthenticated, currentClientId, service, attempt, clientClaimKey, getAccessTokenSilently, memberSyncing]);

    // Safety net for the splash-while-syncing hold below: start a timer while the
    // member sync is in flight, and once it exceeds SYNC_SPLASH_MAX_MS allow the
    // error screen to show even if the sync hasn't settled. Reset when the sync
    // is idle so a later re-sync gets a fresh grace window.
    useEffect(() => {
        setSyncWaitExpired(false);
        if (!memberSyncing) return;
        const timer = setTimeout(() => setSyncWaitExpired(true), SYNC_SPLASH_MAX_MS);
        return () => clearTimeout(timer);
    }, [memberSyncing]);

    const handleRetry = useCallback(() => {
        // Let an explicit Retry attempt a fresh forced refresh too.
        triedForcedRefresh.current = false;
        setAttempt((n) => n + 1);
    }, []);

    // Escape hatch for the "selected tenant doesn't exist (or user has no
    // permissions on it) and every retry re-uses the same broken value from
    // localStorage" loop. Clearing currentClientName lets ClientProvider
    // re-resolve from the Auth0-claims list / slug on the next mount.
    // Lands on /fortius/clients (outside this gate) so even if the next
    // resolved tenant is also broken, the super-admin can still pick a
    // working one from the list instead of bouncing back to this screen.
    const handleSwitchClub = useCallback(() => {
        localStorage.removeItem('currentClientName');
        window.location.assign('/fortius/clients');
    }, []);

    const handleLogout = useCallback(() => {
        logout({ logoutParams: { returnTo: window.location.origin } });
    }, [logout]);

    // "Register with this club": add the current tenant to the user's Auth0
    // clients list, force a token refresh so the new `clients` claim lands, then
    // retry the config load. On success the app renders and the normal onboarding
    // redirect (App.tsx) takes the user into the self-service member flow. On a
    // guard/eligibility failure the service surfaces the server message and
    // throws, so we simply drop back to the error screen.
    const handleRegister = useCallback(async () => {
        setConfirmOpen(false);
        setRegistering(true);
        try {
            const { requiresTokenRefresh } = await service.registerCurrentClient();
            if (requiresTokenRefresh) {
                try {
                    // Causal refresh: must observe the `clients` claim this
                    // self-registration just wrote, so bypass any in-flight
                    // (pre-write) refresh (FORT-293).
                    await forceTokenRefresh(getAccessTokenSilently, { bypassInFlight: true });
                } catch {
                    // No refresh token / refresh failed — the retry below will
                    // re-surface the error rather than leaving a false success.
                }
            }
            triedForcedRefresh.current = false;
            setHasError(false);
            setAttempt((n) => n + 1);
        } catch {
            // The API client already showed the server's message; stay put.
        } finally {
            setRegistering(false);
        }
    }, [service, getAccessTokenSilently]);

    // Hold the splash while our own config fetch is in flight, OR while
    // MemberContext's post-login sync is still provisioning the tenant and
    // refreshing the token — but only until we actually have a config, and only
    // up to the SYNC_SPLASH_MAX_MS safety net. This is what stops a brand-new
    // signup from seeing a transient "you're not registered" screen during the
    // few seconds the `/auth/sync` bootstrap + token refresh take to complete.
    const holdForSyncSettle = memberSyncing && !syncWaitExpired && !config;
    if (loading || holdForSyncSettle) {
        return (
            <div
                data-testid="client-config-splash"
                style={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fff',
                }}
            >
                <Spin size="large" />
            </div>
        );
    }

    if (hasError || !config) {
        return (
            <div
                data-testid="client-config-error"
                style={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fff',
                }}
            >
                <Result
                    status="error"
                    title="Couldn't load club configuration"
                    subTitle={
                        canSelfRegister
                            ? `You're signed in, but you're not registered with ${currentClientId} yet.`
                            : undefined
                    }
                    extra={
                        <>
                            {canSelfRegister && (
                                <Button
                                    type="primary"
                                    loading={registering}
                                    onClick={() => setConfirmOpen(true)}
                                    data-testid="client-config-register"
                                >
                                    Register with this club
                                </Button>
                            )}
                            <Button
                                type={canSelfRegister ? 'default' : 'primary'}
                                onClick={handleRetry}
                                data-testid="client-config-retry"
                                style={{ marginLeft: canSelfRegister ? 8 : 0 }}
                            >
                                Retry
                            </Button>
                            {isFortiusAdmin && (
                                <Button
                                    onClick={handleSwitchClub}
                                    data-testid="client-config-switch-club"
                                    style={{ marginLeft: 8 }}
                                >
                                    Switch club
                                </Button>
                            )}
                            <Button
                                onClick={handleLogout}
                                data-testid="client-config-logout"
                                style={{ marginLeft: 8 }}
                            >
                                Log out
                            </Button>
                        </>
                    }
                />
                <Modal
                    open={confirmOpen}
                    title="Register with this club?"
                    onCancel={() => setConfirmOpen(false)}
                    footer={[
                        <Button
                            key="no"
                            onClick={() => setConfirmOpen(false)}
                            data-testid="client-config-register-cancel"
                        >
                            No
                        </Button>,
                        <Button
                            key="yes"
                            type="primary"
                            onClick={handleRegister}
                            data-testid="client-config-register-confirm"
                        >
                            Yes, register
                        </Button>,
                    ]}
                >
                    <p data-testid="client-config-register-modal-body">
                        Please confirm you'd like to register with <strong>{currentClientId}</strong>.
                        This adds it to your account so you can set up your membership.
                    </p>
                </Modal>
            </div>
        );
    }

    return (
        <ClientConfigContext.Provider value={config}>
            {children}
        </ClientConfigContext.Provider>
    );
};

export const useClientConfig = (): ClientSettings => {
    const context = React.useContext(ClientConfigContext);
    if (context === undefined) {
        throw new Error('useClientConfig must be used within a ClientConfigProvider');
    }
    return context.settings;
};

export const useClientConfigClientId = (): number => {
    const context = React.useContext(ClientConfigContext);
    if (context === undefined) {
        throw new Error('useClientConfigClientId must be used within a ClientConfigProvider');
    }
    return context.clientId;
};
